import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Customer, Quote } from "@/types/crm";
import { useQuoteForm } from "../../../hooks/useQuoteForm";
import BasicQuoteFields from "./shared/BasicQuoteFields";
import LineItemsSection from "./shared/LineItemsSection";
import QuoteTotalsSection from "./shared/QuoteTotalsSection";

interface QuoteFormProps {
  customers: Customer[];
  onQuoteCreated: () => void;
  onCancel: () => void;
  editingQuote?: Quote;
}


const DEFAULT_LINE_ITEMS = [
  { description: "Cash Purchase Price for Land Contract", quantity: 1, unit_price: 0, line_total: 0 },
  { description: "Less: Deed Transfer Tax (Customer Responsibility)", quantity: 1, unit_price: -1000, line_total: -1000 },
  { description: "Less: Appraisal Fee (We Pay)", quantity: 1, unit_price: -500, line_total: -500 },
  { description: "Less: Title Work (We Pay)", quantity: 1, unit_price: -300, line_total: -300 },
  { description: "Less: Closing Fee (We Pay)", quantity: 1, unit_price: -200, line_total: -200 }
];

const QuoteForm = ({ customers, onQuoteCreated, onCancel, editingQuote }: QuoteFormProps) => {
  const [notes, setNotes] = useState(editingQuote?.notes || "");
  
  const {
    formData,
    lineItems,
    isLoading,
    updateFormData,
    updateLineItem,
    addLineItem,
    removeLineItem,
    calculateTotals,
    validateForm,
    submitQuote,
  } = useQuoteForm({
    defaultLineItems: DEFAULT_LINE_ITEMS,
    onSuccess: onQuoteCreated,
    editingQuote,
  });


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    await submitQuote({ notes });
  };

  const { subtotal, total } = calculateTotals();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{editingQuote ? "Edit Quote" : "Create New Quote"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <BasicQuoteFields
            customers={customers}
            formData={formData}
            onUpdateFormData={updateFormData}
          />

          <LineItemsSection
            lineItems={lineItems}
            onUpdateLineItem={updateLineItem}
            onRemoveLineItem={removeLineItem}
            onAddLineItem={addLineItem}
          />

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes"
              rows={2}
            />
          </div>

          {/* Disclaimer Section */}
          <div className="bg-muted/50 p-4 rounded-lg border border-muted">
            <h4 className="font-medium text-sm mb-2">Important Disclaimer:</h4>
            <p className="text-sm text-muted-foreground">
              This quote is subject to due diligence confirmation of all contract terms and conditions. 
              Final pricing depends on successful completion of title work and property appraisal. 
              The actual purchase price may vary based on our findings during the due diligence process.
            </p>
          </div>

          <QuoteTotalsSection subtotal={subtotal} total={total} />

          <div className="flex gap-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (editingQuote ? "Updating..." : "Creating...") : (editingQuote ? "Update Quote" : "Create Quote")}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default QuoteForm;